'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import MainHeader from '@/components/layout/MainHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

export default function ContactNewPage() {
  const router = useRouter();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [replyPhone, setReplyPhone] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const phoneFormat = (raw: string) => {
    const n = raw.replace(/\D/g, '');
    if (n.length <= 3) return n;
    if (n.length <= 7) return `${n.slice(0, 3)}-${n.slice(3)}`;
    return `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7, 11)}`;
  };

  const handleSubmit = async () => {
    if (title.trim().length < 2) {
      setError('제목은 2자 이상 입력해주세요');
      return;
    }
    if (content.trim().length < 5) {
      setError('내용은 5자 이상 입력해주세요');
      return;
    }
    if (replyPhone && !/^01[016789]-\d{3,4}-\d{4}$/.test(replyPhone)) {
      setError('연락처 형식이 올바르지 않습니다');
      return;
    }
    if (isPrivate && !/^\d{4}$/.test(password)) {
      setError('비밀번호는 숫자 4자리를 입력해주세요');
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      const res = await fetch('/api/questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          content,
          isPrivate,
          ...(replyPhone ? { replyPhone } : {}),
          ...(isPrivate ? { password } : {}),
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error || '등록에 실패했습니다');
        return;
      }

      router.push('/contact');
    } catch {
      setError('등록 중 오류가 발생했습니다');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <MainHeader />
      <div className="max-w-[600px] mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Link
            href="/contact"
            className="text-gray-500 hover:text-gray-900 transition-colors"
          >
            ←
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">문의 작성</h1>
        </div>

        <div className="space-y-5">
          <div>
            <Label htmlFor="title" className="text-sm font-medium text-gray-700 mb-1.5 block">
              제목<span className="text-red-500 ml-0.5">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="제목을 입력해주세요"
            />
          </div>

          <div>
            <Label htmlFor="content" className="text-sm font-medium text-gray-700 mb-1.5 block">
              내용<span className="text-red-500 ml-0.5">*</span>
            </Label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="문의 내용을 입력해주세요"
              rows={6}
            />
          </div>

          <div>
            <Label htmlFor="reply-phone" className="text-sm font-medium text-gray-700 mb-1.5 block">
              답변 받을 연락처 (선택)
            </Label>
            <Input
              id="reply-phone"
              type="tel"
              inputMode="numeric"
              value={replyPhone}
              onChange={(e) => setReplyPhone(phoneFormat(e.target.value))}
              placeholder="010-0000-0000"
              maxLength={13}
            />
            <p className="text-xs text-gray-400 mt-1">입력하시면 답변 등록 시 문자로 안내드립니다.</p>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="private"
              checked={isPrivate}
              onCheckedChange={(checked) => {
                setIsPrivate(checked === true);
                if (!checked) setPassword('');
              }}
            />
            <Label htmlFor="private" className="text-sm text-gray-700 cursor-pointer">
              비밀글로 등록
            </Label>
          </div>

          {isPrivate && (
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-gray-700 mb-1.5 block">
                비밀번호 (숫자 4자리)
              </Label>
              <Input
                id="password"
                type="password"
                maxLength={4}
                pattern="[0-9]*"
                inputMode="numeric"
                value={password}
                onChange={(e) => setPassword(e.target.value.replace(/\D/g, ''))}
                placeholder="숫자 4자리"
                className="max-w-[200px]"
              />
            </div>
          )}

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full py-3 bg-[#1A3A5C] hover:bg-[#15304D] text-white"
          >
            {submitting ? '등록중...' : '등록하기'}
          </Button>
        </div>
      </div>
    </div>
  );
}
